import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Stack,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    LocalShipping as ShippingIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Person as PersonIcon,
    LocationOn as LocationIcon,
    Scale as ScaleIcon,
    Receipt as ReceiptIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { customerBookingService } from '../../services/customerBookingService';
import { useApiHandler } from '../../hooks/useApiHandler';

const CustomerBookings = () => {
    const { handleApiCallWithLoading, showError, showSuccess } = useApiHandler();
    
    // State management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
    });
    
    // Filters
    const [filters, setFilters] = useState({
        status: '',
        from_date: '',
        to_date: '',
        search: ''
    });
    
    // Booking details modal
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [bookingDetails, setBookingDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    // Load bookings on component mount
    useEffect(() => {
        loadBookings();
    }, [pagination.current_page, pagination.per_page]);

    const loadBookings = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const params = {
                page: pagination.current_page,
                per_page: pagination.per_page,
                ...filters
            };
            
            const response = await customerBookingService.getMyBookings(params);
            if (response.success) {
                setBookings(response.data);
                if (response.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        ...response.pagination
                    }));
                }
            } else {
                setError(response.message || 'Failed to load bookings');
            }
        } catch (err) {
            console.error('Bookings loading error:', err);
            setError('Failed to load bookings. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, current_page: 1 }));
        loadBookings();
    };

    const handlePageChange = (event, page) => {
        setPagination(prev => ({ ...prev, current_page: page }));
    };

    const handleViewBooking = async (booking) => {
        setSelectedBooking(booking);
        setDetailsModalOpen(true);
        setDetailsLoading(true);
        
        try {
            console.log('Fetching booking details for ID:', booking.booking_id);
            
            // Fetch both booking details and rate calculation
            const [bookingResponse, rateResponse] = await Promise.allSettled([
                customerBookingService.getBooking(booking.booking_id),
                customerBookingService.recalculateRate(booking.booking_id)
            ]);
            
            console.log('Booking details response:', bookingResponse);
            console.log('Rate calculation response:', rateResponse);
            
            if (bookingResponse.status === 'fulfilled' && bookingResponse.value.success) {
                const bookingData = bookingResponse.value.data;
                
                // If rate calculation was successful, merge the data
                if (rateResponse.status === 'fulfilled' && rateResponse.value.success) {
                    const rateData = rateResponse.value.data;
                    bookingData.rate_calculation = {
                        zone_name: rateData.rate_details?.zone_name || null,
                        rate_type: rateData.rate_details?.rate_type || null,
                        base_rate: rateData.rate_details?.base_rate || null,
                        default_weight_range: rateData.rate_details?.default_weight_range || null,
                        additional_charge_display: rateData.rate_details?.additional_charge_display || null,
                        package_discount: rateData.rate_details?.package_disc || 0,
                        fuel_discount: rateData.rate_details?.fuel_surcharge_disc || 0,
                    };
                }
                
                setBookingDetails(bookingData);
            } else {
                console.error('API returned error:', bookingResponse);
                showError(bookingResponse.value?.message || 'Failed to load booking details');
            }
        } catch (err) {
            console.error('Error loading booking details:', err);
            showError('Failed to load booking details. Please try again.');
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleCloseDetails = () => {
        setDetailsModalOpen(false);
        setSelectedBooking(null);
        setBookingDetails(null);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered':
                return 'success';
            case 'out for delivery':
                return 'warning';
            case 'in transit':
                return 'info';
            case 'picked up':
                return 'primary';
            case 'pending':
                return 'default';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    if (loading && bookings.length === 0) {
        return (
            <DashboardLayout title="My Bookings" userType="customer">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout title="My Bookings" userType="customer">
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
                <Button variant="contained" onClick={loadBookings}>
                    Retry
                </Button>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="My Bookings" userType="customer">
            <Grid container spacing={3}>
                {/* Filters */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Search AWB"
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                    >
                                        <MenuItem value="">All Status</MenuItem>
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="picked_up">Picked Up</MenuItem>
                                        <MenuItem value="in_transit">In Transit</MenuItem>
                                        <MenuItem value="out_for_delivery">Out for Delivery</MenuItem>
                                        <MenuItem value="delivered">Delivered</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    value={filters.from_date}
                                    onChange={(e) => handleFilterChange('from_date', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    value={filters.to_date}
                                    onChange={(e) => handleFilterChange('to_date', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSearch}
                                        startIcon={<SearchIcon />}
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={loadBookings}
                                        startIcon={<RefreshIcon />}
                                    >
                                        Refresh
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Bookings Table */}
                <Grid item xs={12}>
                    <Paper>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>AWB Number</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Route</TableCell>
                                        <TableCell>Weight</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {bookings.map((booking) => (
                                        <TableRow key={booking.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {String(booking.awb_no || 'N/A')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(booking.booking_date)}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {String(booking.from_city?.name || booking.frm_city || 'N/A')} → {String(booking.to_city?.name || booking.to_city || 'N/A')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {String(booking.billing?.charged_weight || booking.charged_wt || '0')} kg
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {formatCurrency(booking.pricing?.grand_total || booking.grand_total || 0)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewBooking(booking)}
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {bookings.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <ShippingIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    No Bookings Found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    You haven't created any bookings yet.
                                </Typography>
                            </Box>
                        )}

                        {/* Pagination */}
                        {pagination.last_page > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <Pagination
                                    count={pagination.last_page}
                                    page={pagination.current_page}
                                    onChange={handlePageChange}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Booking Details Modal */}
            <Dialog
                open={detailsModalOpen}
                onClose={handleCloseDetails}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Booking Details - {selectedBooking?.awb_no}
                        </Typography>
                        <IconButton onClick={handleCloseDetails} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {detailsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : bookingDetails ? (
                        <Box>
                            {/* Booking Status */}
                            <Card sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">
                                        AWB Number: <strong>{bookingDetails.awb_no}</strong>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Booking Date: <strong>{formatDate(bookingDetails.booking_date)}</strong>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Product Type: <strong>{bookingDetails.billing?.package_name || bookingDetails.package_name || 'N/A'}</strong>
                                    </Typography>
                                </CardContent>
                            </Card>

                            <Grid container spacing={3}>
                                {/* Route Information */}
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LocationIcon color="primary" />
                                                Route Information
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="text.secondary">From</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {bookingDetails.from_city?.name || bookingDetails.frm_city || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {bookingDetails.shipper?.name || 'N/A'}
                                                    {bookingDetails.shipper?.company && ` - ${bookingDetails.shipper.company}`}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {bookingDetails.shipper?.address || 'N/A'}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">To</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {bookingDetails.to_city?.name || bookingDetails.to_city || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {bookingDetails.recipient?.name || 'N/A'}
                                                    {bookingDetails.recipient?.company && ` - ${bookingDetails.recipient.company}`}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {bookingDetails.recipient?.address || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Package Information */}
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ScaleIcon color="primary" />
                                                Package Information
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            
                                            <List dense>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <ScaleIcon />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="Weight"
                                                        secondary={`${bookingDetails.billing?.charged_weight || bookingDetails.charged_wt || '0'} kg`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <ShippingIcon />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="No of Packages"
                                                        secondary={`${bookingDetails.billing?.no_of_packs || bookingDetails.no_of_pack || '0'} piece(s)`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <ScheduleIcon />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="Transport Mode"
                                                        secondary={bookingDetails.transport || 'N/A'}
                                                    />
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Pricing Information */}
                                <Grid item xs={12}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ReceiptIcon color="primary" />
                                                Rate Calculation Breakdown
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            
                                            {/* Rate Calculation Details */}
                                            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Rate Calculation Details
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2">
                                                            <strong>Billing Mode:</strong> {bookingDetails.billing?.bill_by || 'N/A'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2">
                                                            <strong>Weight Used:</strong> {bookingDetails.billing?.charged_weight || bookingDetails.charged_wt || '0'} kg
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2">
                                                            <strong>Product Type:</strong> {bookingDetails.billing?.package_name || bookingDetails.package_name || 'N/A'}
                                                        </Typography>
                                                    </Grid>
                                                    {bookingDetails.rate_calculation?.zone_name && (
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Zone:</strong> {bookingDetails.rate_calculation.zone_name}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                    {bookingDetails.rate_calculation?.rate_type && (
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Rate Type:</strong> {bookingDetails.rate_calculation.rate_type === 'default' ? 'Default Rate' : 'Extended Rate'}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                    {bookingDetails.rate_calculation?.default_weight_range && (
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Default Weight Range:</strong> {bookingDetails.rate_calculation.default_weight_range.from}-{bookingDetails.rate_calculation.default_weight_range.to} kg
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                    {bookingDetails.rate_calculation?.base_rate && (
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Base Rate:</strong> {formatCurrency(bookingDetails.rate_calculation.base_rate)}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                    {bookingDetails.rate_calculation?.additional_charge_display && (
                                                        <Grid item xs={12}>
                                                            <Typography variant="body2">
                                                                <strong>Additional Charge:</strong> {bookingDetails.rate_calculation.additional_charge_display}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                    {((bookingDetails.rate_calculation?.package_discount || 0) > 0 || (bookingDetails.rate_calculation?.fuel_discount || 0) > 0) && (
                                                        <Grid item xs={12}>
                                                            <Typography variant="body2">
                                                                <strong>Discounts Applied:</strong> Basic Freight {bookingDetails.rate_calculation?.package_discount || 0}%, Fuel Surcharge {bookingDetails.rate_calculation?.fuel_discount || 0}%
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>

                                            {/* Detailed Charges Breakdown */}
                                            <Grid container spacing={2}>
                                                {/* Basic Charges */}
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Basic Charges
                                                    </Typography>
                                                    <Box sx={{ pl: 2 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                            <Typography variant="body2">Basic Freight:</Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {formatCurrency(bookingDetails.pricing?.basic_freight || bookingDetails.basic_freight || 0)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                            <Typography variant="body2">Fuel Surcharge:</Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {formatCurrency(bookingDetails.pricing?.fuel_surcharge || bookingDetails.fuel_surcharge || 0)}
                                                            </Typography>
                                                        </Box>
                                                        <Divider sx={{ my: 1 }} />
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="body2" fontWeight="bold">Subtotal:</Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {formatCurrency(
                                                                    (bookingDetails.pricing?.basic_freight || bookingDetails.basic_freight || 0) +
                                                                    (bookingDetails.pricing?.fuel_surcharge || bookingDetails.fuel_surcharge || 0)
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>

                                                {/* Additional Charges - Only show if there are any additional charges */}
                                                {(() => {
                                                    const additionalCharges = bookingDetails.pricing?.additional_charges || {};
                                                    const totalAdditionalCharges = 
                                                        (additionalCharges.handling || 0) +
                                                        (additionalCharges.statistical || 0) +
                                                        (additionalCharges.to_pay || 0) +
                                                        (additionalCharges.cod_charges || 0) +
                                                        (additionalCharges.special_service || 0) +
                                                        (additionalCharges.value_surcharge || 0) +
                                                        (additionalCharges.carriers_risk || 0) +
                                                        (additionalCharges.remote_area || 0);
                                                    
                                                    if (totalAdditionalCharges > 0) {
                                                        return (
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                    Additional Charges
                                                                </Typography>
                                                                <Box sx={{ pl: 2 }}>
                                                                    {(additionalCharges.handling || 0) > 0 && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="body2">Handling Charges:</Typography>
                                                                            <Typography variant="body2">
                                                                                {formatCurrency(additionalCharges.handling || 0)}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    {(additionalCharges.statistical || 0) > 0 && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="body2">Statistical Charges:</Typography>
                                                                            <Typography variant="body2">
                                                                                {formatCurrency(additionalCharges.statistical || 0)}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    {(additionalCharges.to_pay || 0) > 0 && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="body2">To Pay Surcharge:</Typography>
                                                                            <Typography variant="body2">
                                                                                {formatCurrency(additionalCharges.to_pay || 0)}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    {(additionalCharges.cod_charges || 0) > 0 && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="body2">COD Charges:</Typography>
                                                                            <Typography variant="body2">
                                                                                {formatCurrency(additionalCharges.cod_charges || 0)}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    {(additionalCharges.special_service || 0) > 0 && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="body2">Special Service:</Typography>
                                                                            <Typography variant="body2">
                                                                                {formatCurrency(additionalCharges.special_service || 0)}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    {(additionalCharges.value_surcharge || 0) > 0 && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="body2">Value Surcharge:</Typography>
                                                                            <Typography variant="body2">
                                                                                {formatCurrency(additionalCharges.value_surcharge || 0)}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    {(additionalCharges.carriers_risk || 0) > 0 && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="body2">Carrier's Risk:</Typography>
                                                                            <Typography variant="body2">
                                                                                {formatCurrency(additionalCharges.carriers_risk || 0)}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    {(additionalCharges.remote_area || 0) > 0 && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="body2">Remote Area:</Typography>
                                                                            <Typography variant="body2">
                                                                                {formatCurrency(additionalCharges.remote_area || 0)}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    <Divider sx={{ my: 1 }} />
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                        <Typography variant="body2" fontWeight="bold">Additional Total:</Typography>
                                                                        <Typography variant="body2" fontWeight="bold">
                                                                            {formatCurrency(totalAdditionalCharges)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Grid>
                                                        );
                                                    }
                                                    return null;
                                                })()}

                                                {/* Tax and Total */}
                                                <Grid item xs={12}>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                        <Typography variant="body2">GST (18%):</Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {formatCurrency(bookingDetails.pricing?.gst || bookingDetails.ser_tax || 0)}
                                                        </Typography>
                                                    </Box>
                                                    <Divider sx={{ my: 1 }} />
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="h6">Grand Total:</Typography>
                                                        <Typography variant="h5" color="primary" fontWeight="bold">
                                                            {formatCurrency(bookingDetails.pricing?.grand_total || bookingDetails.grand_total || 0)}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : (
                        <Alert severity="error">
                            Failed to load booking details. Please try again.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetails} variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
};

export default CustomerBookings;
